import { STATUS } from "@/constants/statuses";
import ReviewEntity from "@apps/reviews/entities/ReviewEntity";
import ReviewSchema from "@apps/reviews/schemas/ReviewSchema";
import {
    CustomerAttributeContent,
    ProductAttributeContent,
} from "@apps/reviews/types/index";
import { applyTransaction } from "@datorama/akita";
import { JsonMimeType } from "@lib/ApiClient";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import {
    BadRequestException,
    ForbiddenException,
} from "@lib/ApiClient/exceptions";
import { Observable } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { create, number, object, string } from "superstruct";
import { injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import { LEAVE_REVIEW_SOURCE } from "../constants";
import LeaveReviewStore from "../stores/LeaveReviewStore";

const ErrorSchema = object({
    error: string(),
    message: string(),
    statusCode: number(),
});

export type UploadImage = {
    key: string;
    bucket: string;
};

export type LeaveReviewAttribute = {
    customerAttributes: {
        commonQuestionId: string;
        content: CustomerAttributeContent;
    }[];
    productAttributes: {
        commonQuestionId: string;
        content: ProductAttributeContent;
    }[];
};

export type LeaveReviewRequestBody = {
    productId?: number;
    title?: string;
    body: string;
    rating: number;
    images?: UploadImage[];
    guestEmail?: string;
    guestName?: string;
    customerDisplayNamePolicy?: string;
    attributes?: LeaveReviewAttribute | undefined;
    widget?: LEAVE_REVIEW_SOURCE;
    reviewRequestToken?: string;
};

export type CanLeaveReviewRequestParamsDTO = {
    productId?: number;
    reviewRequestToken?: string;
};

@injectable()
class LeaveReviewService {
    constructor(
        private leaveReviewStore: LeaveReviewStore,
        private readonly authApiClient: ApiClientInterface,
        private readonly logger: LoggerInterface
    ) {}

    leaveReview(reqBody: LeaveReviewRequestBody): Observable<ReviewEntity> {
        this.logger.debug("ReviewsService.leaveReview is started");
        this.leaveReviewStore.update((state) => {
            return {
                ...state,
                leaveReviewStatus: STATUS.LOADING,
            };
        });
        return this.authApiClient
            .post({
                url: "/review/leaveReview",
                body: JSON.stringify(reqBody),
                responseType: "json",
                contentType: JsonMimeType,
            })
            .pipe(
                map((review) => create(review.body, ReviewSchema)),
                catchError((error: unknown) => {
                    this.logger.debug("ReviewsService.leaveReview is errored", {
                        reqBody,
                        error,
                    });

                    applyTransaction(() => {
                        this.leaveReviewStore.update({
                            leaveReviewStatus: STATUS.ERROR,
                        });

                        if (
                            error instanceof BadRequestException ||
                            error instanceof ForbiddenException
                        ) {
                            const result = create(
                                error.response.body as string,
                                ErrorSchema
                            );
                            this.leaveReviewStore.update({
                                leaveReviewError: result as unknown as Error,
                            });
                        }
                    });
                    throw error;
                }),
                tap((review) => {
                    this.logger.debug(
                        "ReviewsService.leaveReview is ready",
                        reqBody
                    );
                    applyTransaction(() => {
                        this.leaveReviewStore.update((state) => {
                            return {
                                ...state,
                                review,
                                leaveReviewStatus: STATUS.READY,
                                leaveReviewCount: state.leaveReviewCount + 1,
                            };
                        });
                    });
                })
            );
    }
}

export default LeaveReviewService;
